import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import GoogleIcon from "@mui/icons-material/Google";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/login")({
  component: LoginPage,
  head: () => ({
    meta: [
      { title: "Log in — JobYoda" },
      { name: "description", content: "Sign in to your JobYoda account to continue scoring jobs, generating tailored materials, and tracking your pipeline." },
      { property: "og:title", content: "Log in — JobYoda" },
      { property: "og:description", content: "Sign in to your JobYoda account." },
      { property: "og:type", content: "website" },
    ],
  }),
});

function LoginPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate({ to: "/app/profile" });
  }, [user, loading, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get("dollyland_error");
    if (err) {
      toast.error(`Dollyland sign-in: ${err}`);
      const url = new URL(window.location.href);
      url.searchParams.delete("dollyland_error");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (err) toast.error(err.message);
    else navigate({ to: "/app/profile" });
  };

  const handleGoogle = async () => {
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}/app/profile`,
    });
    if (result.error) toast.error(result.error.message);
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "background.default", p: 2 }}>
      <Container component="main" maxWidth="xs" disableGutters>
        <Card>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700 }}>
              Welcome back
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Sign in to continue to JobYoda.
            </Typography>

            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                window.location.href = "/api/public/auth/dollyland/start?next=/app/profile";
              }}
              sx={{ mb: 1.5 }}
            >
              Sign in with Dollyland
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={handleGoogle}
              sx={{ mb: 2 }}
            >
              Continue with Google
            </Button>
            <Divider sx={{ my: 2 }}>or</Divider>

            <Stack component="form" spacing={2} onSubmit={handleEmailLogin}>
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                fullWidth
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                fullWidth
              />
              <Button type="submit" variant="contained" size="large" disabled={submitting}>
                {submitting ? "Signing in…" : "Sign in"}
              </Button>
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: "center" }}>
              Don't have an account?{" "}
              <Link to="/signup" style={{ color: "inherit", fontWeight: 600 }}>
                Create one
              </Link>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block", textAlign: "center" }}>
              <Link to="/terms" style={{ color: "inherit" }}>Terms</Link>
              {" · "}
              <Link to="/support" style={{ color: "inherit" }}>Support</Link>
            </Typography>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
